import { useState } from "react";
import {
  AlertIcon,
  CheckIcon,
  DashButton,
  Field,
  InfoIcon,
  Input,
  Note,
  Select,
} from "../../../shared/components";
import {
  addStudent,
  linkPlayerParent,
  movePlayerClass,
  removeFromClass,
  setPlayerName,
} from "../../../shared/api/student";
import type { StaffAuth } from "../../../shared/api/teaching";
import type { ClassSummary } from "../../../shared/api/classes";
import { classLabel } from "./classChoice";
import { STUDENT_COPY } from "./studentCopy";
import type { Lang } from "./copy";

/**
 * The three register controls a teacher never had.
 *
 * 🔴 THE PRODUCT COULD ONLY CREATE A CHILD FROM A SPREADSHEET. `POST
 * /roster/import` is ADMIN-only against threat T8 — it moves hundreds of parent
 * phone numbers behind one audit row — and it was the ONLY path that has ever
 * written a `Player`. So a teacher who gained a child in October had to find an
 * admin, who had to re-export a file, in order to write down a name; and a child
 * enrolled into the wrong section stayed there. The register drifts from the
 * room, and every screen downstream of it is quietly wrong.
 *
 * ✅ THE PARENT'S DETAILS *ARE* HERE NOW, AND THIS PARAGRAPH USED TO SAY THE
 * OPPOSITE. It read *"the family's phone number is the office's record… a body
 * carrying a phone number needs `write` on the class, which only an admin has"*,
 * and ADR-050 overturned exactly that: the permission moved to `roster` because
 * treating *"write down the family's number"* as an act of administration is
 * what kept the register drifting from the room. `AddStudentForm` had already
 * been rewritten to draw the fields — only this header still described the old
 * split, which is the most expensive kind of comment to leave standing.
 *
 * ⚠️ THE BULK PATH IS STILL ADMIN-ONLY. `/roster/import` moves hundreds of
 * parent phone numbers behind one audit row — threat T8 — and that is a
 * different act from one teacher typing one number for the child in front of
 * her. Both routes here are one child at a time, and audited per child.
 *
 * ⚠️ `StudentPage` USED TO CARRY A NOTE SAYING THE MOVE CONTROL *"IS
 * DELIBERATELY NOT OFFERED HERE"* because the route needed class WRITE scope,
 * *"and no admin can reach this screen"*. That was an accurate description of a
 * dead end: S5's *"mid-year reshuffles happen constantly"* had a built, scoped,
 * tested endpoint and no user. The scope has a `roster` level now, and the note
 * has become this component.
 */

/** Shared shape: run a write, report the outcome, tell the page to refetch. */
function useWrite(onChanged?: () => void) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const run = (work: () => Promise<unknown>): void => {
    setBusy(true);
    setFailed(null);
    setDone(false);
    void work()
      .then(() => {
        setDone(true);
        onChanged?.();
      })
      .catch((err: unknown) => {
        /*
         * 🔴 THE SERVER'S SENTENCE, WHEN IT WROTE ONE. A roll-number collision
         * answers *"Roll number 3 is already used by Meera in that class"* — the
         * one message a teacher can act on without asking anybody. `client.ts`
         * puts it on the error; a generic "that did not save" would throw it away
         * and leave her guessing which of thirty children is in the way.
         */
        const message = err instanceof Error ? err.message : "";
        setFailed(message.length > 0 ? message : null);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return {
    busy,
    failed,
    done,
    run,
    reset: () => {
      setDone(false);
      setFailed(null);
    },
  };
}

/* ─────────────────────────────────────────────────────────── move class ── */

export function MoveClassPanel({
  playerId,
  classId,
  classes,
  lang = "en",
  auth,
  onChanged,
}: {
  playerId: string;
  /** The class they are in now — excluded from the picker. */
  classId: string;
  classes: readonly ClassSummary[];
  lang?: Lang;
  auth?: StaffAuth;
  onChanged?: () => void;
}) {
  const s = STUDENT_COPY[lang];
  const [toClassId, setToClassId] = useState("");
  const [roll, setRoll] = useState("");
  const write = useWrite(onChanged);

  /*
   * ⚠️ ONLY THE CLASSES THIS TEACHER TEACHES, AND NEVER THE CURRENT ONE. The
   * list is `classes` — what the server already told this teacher she has — so
   * the picker cannot express a move the server would refuse. Offering the
   * current class would be offering a no-op the service quietly absorbs.
   */
  const options = classes.filter(
    (c) => c.id !== classId && c.archivedAt === null,
  );

  if (options.length === 0) {
    /* A teacher with one section has nowhere to move a child TO, and a picker
       with no options is a control that reads as broken. */
    return null;
  }

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <h2 className="mb-1 font-dash text-dash-md font-extrabold text-ink">
        {s.moveTitle}
      </h2>
      <p className="mb-4 text-dash-xs text-muted">{s.moveWhy}</p>

      <div className="flex flex-col gap-3">
        <Field label={s.moveTo} htmlFor="move-class" width="full">
          <Select
            id="move-class"
            value={toClassId}
            onChange={(e) => {
              setToClassId(e.target.value);
              write.reset();
            }}
          >
            <option value="">{s.movePick}</option>
            {options.map((c) => (
              <option key={c.id} value={c.id}>
                {classLabel(c)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={s.moveRoll}
          hint={s.moveRollWhy}
          htmlFor="move-roll"
          width="full"
        >
          <Input
            id="move-roll"
            value={roll}
            maxLength={16}
            onChange={(e) => setRoll(e.target.value)}
          />
        </Field>

        <div>
          <DashButton
            variant="primary"
            disabled={write.busy || toClassId === ""}
            onClick={() => {
              write.run(() =>
                movePlayerClass(
                  playerId,
                  {
                    classId: toClassId,
                    /* ⚠️ ABSENT AND EMPTY DIFFER AT THE SERVICE: absent keeps the
                       current roll, and this form's blank field means "keep". */
                    ...(roll.trim() === "" ? {} : { rollNumber: roll.trim() }),
                  },
                  auth,
                ),
              );
            }}
          >
            {s.move}
          </DashButton>
        </div>

        {write.done && (
          <Note tone="success" icon={<CheckIcon size={16} />}>
            {s.moved}
          </Note>
        )}
        {write.failed !== null && (
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {write.failed}
          </Note>
        )}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────── off the register ── */

export function RemoveFromClassPanel({
  playerId,
  lang = "en",
  auth,
  onChanged,
}: {
  playerId: string;
  lang?: Lang;
  auth?: StaffAuth;
  onChanged?: () => void;
}) {
  const s = STUDENT_COPY[lang];
  const [confirming, setConfirming] = useState(false);
  const write = useWrite(onChanged);

  return (
    <div className="rounded-2xl border border-line bg-surface p-4 sm:p-6 shadow-card">
      <h2 className="mb-1 font-dash text-dash-md font-extrabold text-ink">
        {s.removeTitle}
      </h2>
      <p className="mb-3 text-dash-xs text-muted">{s.removeWhy}</p>

      {/* 🔴 THE SENTENCE THAT STOPS A TEACHER THINKING THIS IS A DELETE. "Remove"
          is the word she will reach for and the wrong one for what happens. */}
      <div className="mb-4">
        <Note tone="neutral" icon={<InfoIcon size={16} />}>
          {s.removeNotDelete}
        </Note>
      </div>

      <div className="flex flex-wrap gap-2">
        {confirming ? (
          <>
            {/*
              Two taps. Not because this is irreversible — it is not, an admin can
              place the child again — but because it is the one control on this
              page that makes a child vanish from the screen the teacher is
              looking at, and a mis-tap mid-lesson is expensive to understand.
            */}
            <DashButton
              variant="destructive"
              disabled={write.busy}
              onClick={() => {
                write.run(() => removeFromClass(playerId, auth));
                setConfirming(false);
              }}
            >
              {s.removeConfirm}
            </DashButton>
            <DashButton variant="ghost" onClick={() => setConfirming(false)}>
              {s.cancel}
            </DashButton>
          </>
        ) : (
          <DashButton variant="destructive" onClick={() => setConfirming(true)}>
            {s.removeAction}
          </DashButton>
        )}
      </div>

      {write.done && (
        <div className="mt-3">
          <Note tone="success" icon={<CheckIcon size={16} />}>
            {s.removeDone}
          </Note>
        </div>
      )}
      {write.failed !== null && (
        <div className="mt-3">
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {write.failed}
          </Note>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────── add a child ── */

export interface AddStudentFormProps {
  classId: string;
  lang?: Lang;
  auth?: StaffAuth;
  onAdded?: () => void;
  /**
   * Draw the family's details — parent name and phone — beside the child's.
   *
   * ✅ BOTH TIERS PASS IT NOW (ADR-050). It used to be the line between the
   * admin's form and the teacher's, because a phone number demanded `write` on
   * the class and only an admin had it. The permission moved to `roster`, so the
   * teacher's form draws the same fields.
   *
   * 🔴 IT STILL DEFAULTS TO `false`, AND THAT IS NOT LEFTOVER. The server decides
   * from the BODY, not from this flag — so a caller that should not be
   * collecting a phone number gets a form that cannot, and D4's rule holds: no
   * screen offers a field whose submit the server would refuse.
   */
  withParent?: boolean;
}

export function AddStudentForm({
  classId,
  lang = "en",
  auth,
  onAdded,
  withParent = false,
}: AddStudentFormProps) {
  const s = STUDENT_COPY[lang];
  const [name, setName] = useState("");
  const [roll, setRoll] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const write = useWrite(onAdded);

  const valid = name.trim().length > 0;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={s.addName} htmlFor="add-name" width="md">
          <Input
            id="add-name"
            value={name}
            maxLength={80}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
        <Field label={s.addRoll} htmlFor="add-roll">
          <Input
            id="add-roll"
            value={roll}
            maxLength={16}
            onChange={(e) => setRoll(e.target.value)}
          />
        </Field>

        {withParent && (
          <>
            <Field label={s.addParentName} htmlFor="add-parent-name" width="md">
              <Input
                id="add-parent-name"
                value={parentName}
                maxLength={80}
                onChange={(e) => setParentName(e.target.value)}
              />
            </Field>
            <Field
              label={s.addParentPhone}
              htmlFor="add-parent-phone"
            >
              <Input
                id="add-parent-phone"
                value={parentPhone}
                maxLength={20}
                inputMode="tel"
                onChange={(e) => setParentPhone(e.target.value)}
              />
            </Field>
          </>
        )}

        <DashButton
          variant="primary"
          disabled={write.busy || !valid}
          onClick={() => {
            write.run(() =>
              addStudent(
                {
                  classId,
                  name: name.trim(),
                  rollNumber: roll.trim() === "" ? null : roll.trim(),
                  /* ⚠️ THE PARENT FIELDS ARE OMITTED ENTIRELY WHEN THE FORM DOES
                     NOT SHOW THEM. Sending empty strings would make every add
                     from a teacher's screen ask for `write` scope and 403. */
                  ...(withParent && parentPhone.trim() !== ""
                    ? {
                        parentName: parentName.trim() || null,
                        parentPhone: parentPhone.trim(),
                      }
                    : {}),
                },
                auth,
              ).then(() => {
                setName("");
                setRoll("");
                setParentName("");
                setParentPhone("");
              }),
            );
          }}
        >
          {s.addAction}
        </DashButton>
      </div>

      {write.done && (
        <Note tone="success" icon={<CheckIcon size={16} />}>
          {s.addDone}
        </Note>
      )}
      {write.failed !== null && (
        <Note tone="warning" icon={<AlertIcon size={16} />}>
          {write.failed}
        </Note>
      )}
    </div>
  );
}

/**
 * Correct one child's name, and write down the family's number.
 *
 * 🔴 TWO SUBMITS, NOT ONE FORM WITH ONE BUTTON — AND THAT MIRRORS THE TWO
 * ROUTES BEHIND IT. `PATCH /players/:id/name` and `PATCH /players/:id/parent`
 * are separate for the reason `/roll` is separate from `/class`: a request that
 * carries a field it did not mean to change is how a stale value edits something
 * nobody touched. One button over both fields would rebuild that in the client —
 * a teacher fixing a spelling would also re-send a phone number, and the parent
 * half is the half that can create an account and move somebody's access.
 *
 * 🔴 THE TWO HALVES ALSO FAIL DIFFERENTLY, WHICH ONE `Note` COULD NOT SAY. A
 * name is refused for length; a number is refused because it is unmessageable,
 * or because it belongs to a member of staff — and that last sentence is the one
 * a teacher must read verbatim to know what to do next. Each half keeps its own
 * `useWrite`, so the message sits under the field that produced it.
 *
 * ⚠️ IT DOES NOT OFFER AN UNLINK, because the server does not. Removing a
 * family's access is a rights change `RightsService` owns, not a register
 * correction; the warning below is what this form says instead.
 */
export interface EditChildPanelProps {
  playerId: string;
  /** The name as the register currently spells it — the field's starting value. */
  name: string;
  /**
   * 🔴 WHETHER A PARENT IS ALREADY LINKED, WHICH DECIDES THE WARNING. The server
   * permits a replacement and audits it as one; a teacher who thinks she is
   * adding a second contact and is in fact displacing the first is the single
   * mistake this form can make that nobody would notice afterwards.
   */
  hasParent: boolean;
  lang?: Lang;
  auth?: StaffAuth;
  onChanged?: () => void;
}

export function EditChildPanel({
  playerId,
  name,
  hasParent,
  lang = "en",
  auth,
  onChanged,
}: EditChildPanelProps) {
  const s = STUDENT_COPY[lang];
  const [nextName, setNextName] = useState(name);
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const renaming = useWrite(onChanged);
  const linking = useWrite(onChanged);

  /*
   * ⚠️ THE SAME 1–80 THE SERVER ENFORCES, AND UNCHANGED COUNTS AS INVALID. A
   * submit that re-sends the name already on the register is a write, an audit
   * read and a refetch to achieve nothing; disabling it is cheaper than a
   * round-trip that reports success for a no-op.
   */
  const trimmedName = nextName.trim();
  const nameReady = trimmedName.length > 0 && trimmedName !== name.trim();
  const phoneReady = parentPhone.trim() !== "";

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={s.addName} htmlFor="edit-name" width="md">
          <Input
            id="edit-name"
            value={nextName}
            maxLength={80}
            onChange={(e) => setNextName(e.target.value)}
          />
        </Field>
        <DashButton
          variant="primary"
          disabled={renaming.busy || !nameReady}
          onClick={() => {
            renaming.run(() => setPlayerName(playerId, trimmedName, auth));
          }}
        >
          {s.editNameAction}
        </DashButton>
      </div>

      {renaming.done && (
        <Note tone="success" icon={<CheckIcon size={16} />}>
          {s.editNameDone}
        </Note>
      )}
      {renaming.failed !== null && (
        <Note tone="warning" icon={<AlertIcon size={16} />}>
          {renaming.failed}
        </Note>
      )}

      <div className="border-t border-line pt-4 flex flex-col gap-3">
        {/*
          🔴 THE WARNING COMES BEFORE THE FIELDS, NOT AFTER THE SUBMIT. A
          consequence explained underneath the button that causes it is a
          consequence read second.
        */}
        {hasParent && (
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {s.editParentReplaceWarn}
          </Note>
        )}

        <div className="flex flex-wrap items-end gap-3">
          <Field label={s.addParentName} htmlFor="edit-parent-name" width="md">
            <Input
              id="edit-parent-name"
              value={parentName}
              maxLength={80}
              onChange={(e) => setParentName(e.target.value)}
            />
          </Field>
          <Field label={s.addParentPhone} htmlFor="edit-parent-phone">
            <Input
              id="edit-parent-phone"
              value={parentPhone}
              maxLength={20}
              inputMode="tel"
              onChange={(e) => setParentPhone(e.target.value)}
            />
          </Field>
          <DashButton
            variant="primary"
            disabled={linking.busy || !phoneReady}
            onClick={() => {
              linking.run(() =>
                linkPlayerParent(
                  playerId,
                  {
                    parentPhone: parentPhone.trim(),
                    /* ⚠️ NAMES A NEW ACCOUNT ONLY — the server never renames a
                       parent who is already in the product, so a blank here is
                       not a request to clear anybody's name. */
                    parentName: parentName.trim() || null,
                  },
                  auth,
                ).then(() => {
                  setParentName("");
                  setParentPhone("");
                }),
              );
            }}
          >
            {s.editParentAction}
          </DashButton>
        </div>

        <Note tone="neutral" icon={<InfoIcon size={16} />}>
          {s.addParentPhoneHint}
        </Note>

        {linking.done && (
          <Note tone="success" icon={<CheckIcon size={16} />}>
            {s.editParentDone}
          </Note>
        )}
        {linking.failed !== null && (
          <Note tone="warning" icon={<AlertIcon size={16} />}>
            {linking.failed}
          </Note>
        )}
      </div>
    </div>
  );
}
